import { useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Tenant } from '@/lib/tenant';
import { cancelMyBooking, fetchMyBookings, rescheduleMyBooking, type MyBooking } from '@/lib/mybookings';
import { fetchSlots, type Slot } from '@/lib/booking';

// Профиль клиента: история записей и статус «куда уже записался».
// Пока без авторизации — в демо показываем записи текущей сессии.
export function ClientProfile() {
  const { tenant } = useOutletContext<{ tenant: Tenant }>();
  const [tab, setTab] = useState<'upcoming' | 'history'>('upcoming');
  const [rescheduling, setRescheduling] = useState<MyBooking | null>(null);
  const queryClient = useQueryClient();
  // Живое обновление подписок идёт из TenantLayout — здесь не дублируем.

  const { data, isLoading } = useQuery({
    queryKey: ['my-bookings', tenant.id],
    queryFn: () => fetchMyBookings(tenant.id)
  });

  const now = Date.now();
  const rows = (data ?? []).filter((b) =>
    tab === 'upcoming'
      ? new Date(b.starts_at).getTime() >= now && !['cancelled', 'done'].includes(b.status)
      : new Date(b.starts_at).getTime() < now || ['cancelled', 'done'].includes(b.status)
  );

  const { data: replacementSlots, isLoading: slotsLoading } = useQuery({
    queryKey: ['reschedule-slots', tenant.id, rescheduling?.id, rescheduling?.service_id],
    queryFn: () => fetchSlots(tenant.id, rescheduling!.service_id),
    enabled: Boolean(rescheduling)
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['my-bookings', tenant.id] });
    await queryClient.invalidateQueries({ queryKey: ['owner-bookings', tenant.id] });
    await queryClient.invalidateQueries({ queryKey: ['slots', tenant.id] });
  };
  const cancelMutation = useMutation({
    mutationFn: cancelMyBooking,
    onSuccess: refresh
  });
  const rescheduleMutation = useMutation({
    mutationFn: ({ id, slot }: { id: string; slot: Slot }) => rescheduleMyBooking(id, slot),
    onSuccess: async () => { setRescheduling(null); await refresh(); }
  });

  return (
    <main className="mnc-page">
      <section className="mnc-profile-head mnc-glass">
        <div className="mnc-avatar">К</div>
        <div>
          <h2 style={{ margin: 0 }}>Мои записи</h2>
          <p className="mnc-muted" style={{ margin: 0 }}>
            {tenant.name}
          </p>
        </div>
      </section>

      <div className="mnc-tabs mnc-glass">
        <button
          className="mnc-tab"
          aria-selected={tab === 'upcoming'}
          onClick={() => setTab('upcoming')}
        >
          Предстоящие
        </button>
        <button
          className="mnc-tab"
          aria-selected={tab === 'history'}
          onClick={() => setTab('history')}
        >
          История
        </button>
      </div>

      <section className="mnc-card" style={{ cursor: 'default' }}>
        {isLoading && <p className="mnc-muted">Загружаем записи…</p>}
        {!isLoading && rows.length === 0 && (
          <div className="mnc-empty">
            {tab === 'upcoming' ? 'Пока нет предстоящих записей.' : 'История пуста.'}
            <div style={{ marginTop: 14 }}>
              <Link className="mnc-btn" to="/book">
                Записаться
              </Link>
            </div>
          </div>
        )}
        <ul className="mnc-list">
          {rows.map((b) => (
            <li key={b.id} className="mnc-booking-history-row">
              <span>
                {b.service_name}
                <br />
                <small className="mnc-muted">
                  {b.staff_name ?? 'Мастер'} · {b.status === 'cancelled' ? 'Отменена' : b.status === 'done' ? 'Завершена' : 'Подтверждена'}
                </small>
              </span>
              <span className="mnc-when">
                {new Date(b.starts_at).toLocaleString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
              {tab === 'upcoming' && (
                <span className="mnc-booking-actions">
                  <button className="mnc-linklike" onClick={() => setRescheduling(b)}>Перенести</button>
                  <button
                    className="mnc-linklike is-danger"
                    disabled={cancelMutation.isPending}
                    onClick={() => {
                      if (window.confirm('Отменить эту запись?')) cancelMutation.mutate(b.id);
                    }}
                  >Отменить</button>
                </span>
              )}
            </li>
          ))}
        </ul>
        {cancelMutation.isError && <p className="mnc-error">{cancelMutation.error.message}</p>}
      </section>

      {rescheduling && (
        <section className="mnc-card mnc-reschedule">
          <div className="mnc-admin-panel-head">
            <div><h2>Перенести запись</h2><p className="mnc-muted">{rescheduling.service_name}</p></div>
            <button className="mnc-linklike" onClick={() => setRescheduling(null)}>Закрыть</button>
          </div>
          {slotsLoading && <p className="mnc-muted">Ищем свободное время…</p>}
          {!slotsLoading && replacementSlots?.length === 0 && <p className="mnc-muted">Свободных окон пока нет.</p>}
          <div className="mnc-reschedule-slots">
            {replacementSlots?.slice(0, 12).map((slot) => (
              <button
                key={`${slot.staffId}-${slot.startsAt}`}
                className="mnc-slot"
                disabled={rescheduleMutation.isPending}
                onClick={() => rescheduleMutation.mutate({ id: rescheduling.id, slot })}
              >
                <b>{new Date(slot.startsAt).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}</b>
                <span>{new Date(slot.startsAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
                <small>{slot.staffName}</small>
              </button>
            ))}
          </div>
          {rescheduleMutation.isError && <p className="mnc-error">{rescheduleMutation.error.message}</p>}
        </section>
      )}

    </main>
  );
}
